import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { stocksApi } from '@/lib/stocksApi'
import { BloodStockTable } from '@/components/BloodStockTable'
import { MedicationStockTable } from '@/components/MedicationStockTable'

export default function StocksPage() {
  const [activeTab, setActiveTab] = useState<'blood' | 'medication'>('blood')

  const { data: bloodItems = [], isLoading: bloodLoading } = useQuery({
    queryKey: ['stocks', 'blood'],
    queryFn: stocksApi.getBlood,
    refetchInterval: 30_000,
  })

  const { data: medicationItems = [], isLoading: medLoading } = useQuery({
    queryKey: ['stocks', 'medication'],
    queryFn: stocksApi.getMedications,
    refetchInterval: 30_000,
  })

  const allItems = [...bloodItems, ...medicationItems]
  const criticalCount = allItems.filter(i => i.status === 'CRITICAL').length
  const warningCount = allItems.filter(i => i.status === 'WARNING').length

  const isLoading = bloodLoading || medLoading

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-neutral-900">Stocks critiques</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Niveaux de stock par structure
        </p>
      </div>

      {/* Alert summary bar */}
      {!isLoading && (
        <div className="flex gap-4 mb-6">
          {criticalCount > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
              ● {criticalCount} alerte{criticalCount > 1 ? 's' : ''} critique{criticalCount > 1 ? 's' : ''}
            </span>
          )}
          {warningCount > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
              ● {warningCount} avertissement{warningCount > 1 ? 's' : ''}
            </span>
          )}
          {criticalCount === 0 && warningCount === 0 && (
            <span className="text-sm text-emerald-700">✓ Tous les stocks sont corrects</span>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-neutral-200">
        <button
          onClick={() => setActiveTab('blood')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'blood'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
        >
          Sang 🩸
        </button>
        <button
          onClick={() => setActiveTab('medication')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'medication'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
        >
          Médicaments 💊
        </button>
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : activeTab === 'blood' ? (
        <BloodStockTable items={bloodItems} />
      ) : (
        <MedicationStockTable items={medicationItems} />
      )}
    </div>
  )
}
